import { create } from "zustand";
import axios from "axios";
import type { AdditionalQuestionsConfig, Status } from "../../types";
import {
  OrganizerPages,
  OrganizerStatus,
} from "../../../src/utils/constants/TaxOrganizer";
import type { ValidationParams, ProgressParams } from "@/types/taxOrganizer";
import {
  fetchOrganizerDataFromAPI,
  fetchSensitiveInformation,
  getUserInfo,
  getNameAndPhone,
  updateIdentityVerification,
  updateSensitiveData,
  saveTaxOrganizerDataToAPI,
} from "./taxOrganizerHelpers";
import { useEngagementLetterStore } from "./engagementLetterStore";
import { backend_url } from "../../constants";
import { AXIOS_CONFIG } from "../api";
import { Endpoints } from "../api/endpoints";

export interface TaxOrganizerData {
  taxYear?: number;
  currentStep: string;
  currentPage: string;
  steps: Record<string, string[]>;
  responses: any;
  docsToUpload: Record<string, number>;
  status: Status;
  editingReviewPage: boolean;
  isDetailedForm: boolean;
  isDocumentProcessing: boolean;
  additionalQuestionsConfig: any;
  setAdditionalQuestionsConfig: (
    questions: TaxOrganizerData["additionalQuestionsConfig"]
  ) => void;
  validationErrors: Record<string, string>;
  additionalQuestionsResponses: any;
  activeTab: string;

  // Async operation states
  isLoadingOrganizer: boolean;
  isSavingProgress: boolean;
  organizerError: Error | null;
  saveError: Error | null;

  // Data from backend
  forms: any;
  allTaxYears: string[];
  organizerCompleteDate: string | null;

  // Metadata
  lastFetchedAt: number | null;
  userId: string | null;

  // Actions
  setTaxYear: (year: number) => void;
  setIsDetailedForm: (isDetailedForm: boolean) => void;
  setIsDocumentProcessing: (isDocumentProcessing: boolean) => void;
  setEditingReviewPage: (editing: boolean) => void;
  goBackToReviewPage: () => void;
  setValidationErrors: (
    errors:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
  updateAdditionalQuestionsResponses: (docs: any) => void;
  setActiveTab: (tab: string) => void;
  setCurrentStep: (step: string) => void;
  setCurrentPage: (page: string) => void;
  updateResponses: (newResponses: Partial<any>) => void;
  updateDocsToUpload: (newDocsToUpload: Partial<any>) => void;
  setStatus: (status: Status) => void;
  setInitialData: (data: Partial<TaxOrganizerData>) => void;
  updateSteps: (responses: any, isDetailedForm?: boolean) => void;
  goToNextPage: () => void;
  getValidationParams: () => ValidationParams;
  getProgressParams: () => ProgressParams;
  resetStore: () => void;

  // Async actions
  fetchOrganizerData: (
    userId: string,
    taxYear: number,
    client?: any
  ) => Promise<void>;
  saveProgress: (
    newResponses: Partial<any>,
    newStatus?: Status,
    newStep?: string,
    newPage?: string
  ) => Promise<void>;
  refetchOrganizer: () => Promise<void>;
  clearOrganizerData: () => void;
}

const processStepConditions = (
  updatedResponses: any,
  currentSteps: any,
  isDetailedForm: boolean,
  additionalQuestions: AdditionalQuestionsConfig
) => {
  const newSteps = [...Object.keys(currentSteps)];
  const newStepPages = { ...currentSteps };

  const stepConditions = [
    {
      name: "Household Details",
      condition: () =>
        updatedResponses?.personalInfo?.[0].filingStatus
          ?.toLowerCase()
          .includes("married") ||
        updatedResponses?.personalInfo?.[0].filingStatus
          ?.toLowerCase()
          .includes("spouse"),
      pages: ["spouseInfo"],
      insertAfter: "Basics",
      fallbackInsertBefore: "Documents",
      detailedOnly: true,
    },
    {
      name: "Dependents",
      condition: () => updatedResponses?.personalInfo?.[0].numDependents > 0,
      pages: ["dependentsInfo"],
      pageConditions: [
        {
          page: "daycareInfo",
          condition: () =>
            updatedResponses?.dependentsInfo?.some(
              (dependent: any) => dependent.dependentsDaycareChoice === "Yes"
            ),
        },
      ],
      insertAfter: "Household Details",
      fallbackInsertBefore: "Documents",
      detailedOnly: true,
    },
    {
      name: "Business Details",
      condition: () =>
        updatedResponses?.personalInfo?.[0].incomeType?.some((item: any) =>
          item.includes("business")
        ),
      pages: ["businessInfo"],
      insertAfter: "Dependents",
      fallbackInsertBefore: "Documents",
      detailedOnly: true,
    },
    {
      name: "Credits and Deductions",
      condition: () =>
        updatedResponses?.docInfo?.[0].tiles?.some(
          (item: number) => item === 8 || item === 9
        ),
      pages: [],
      insertAfter: "Dependents",
      fallbackInsertBefore: "Refund Instructions",
      pageConditions: [
        {
          page: "credits",
          condition: () =>
            updatedResponses?.docInfo?.[0].tiles?.some(
              (item: number) => item === 8
            ),
        },
        {
          page: "medicalExpenses",
          condition: () =>
            updatedResponses?.docInfo?.[0].tiles?.some(
              (item: number) => item === 9
            ),
        },
      ],
      detailedOnly: true,
    },
    {
      name: "Basics",
      condition: () => isDetailedForm,
      pages: ["identityVerification"],
      pageConditions: [
        {
          page: "formerAddress",
          condition: () =>
            updatedResponses?.docInfo?.[0].tiles?.some(
              (item: number) => item === 7
            ),
        },
      ],
      detailedOnly: true,
    },
    {
      name: "Additional Questions",
      condition: () => additionalQuestions?.questions?.length > 0,
      pages: [OrganizerPages.ADDITIONAL_QUESTIONS],
      insertAfter: "Refund Instructions",
      fallbackInsertBefore: "Review",
      detailedOnly: false,
    },
  ];

  stepConditions.forEach(
    ({
      name,
      condition,
      pages,
      insertAfter,
      fallbackInsertBefore,
      pageConditions,
      detailedOnly,
    }) => {
      const shouldInclude =
        (condition ? condition() : true) && (isDetailedForm || !detailedOnly);
      const isIncluded = newSteps.includes(name);

      if (shouldInclude && !isIncluded) {
        let index = newSteps.length;
        if (insertAfter && newSteps.includes(insertAfter)) {
          index = newSteps.indexOf(insertAfter) + 1;
        } else if (
          fallbackInsertBefore &&
          newSteps.includes(fallbackInsertBefore)
        ) {
          index = newSteps.indexOf(fallbackInsertBefore);
        }
        newSteps.splice(index, 0, name);
        newStepPages[name] = pages || [];
      } else if (!shouldInclude && isIncluded) {
        const index = newSteps.indexOf(name);
        newSteps.splice(index, 1);
        delete newStepPages[name];
      }

      if (isIncluded && pageConditions) {
        pageConditions.forEach(({ page, condition }) => {
          if (condition()) {
            if (!newStepPages[name]?.includes(page)) {
              newStepPages[name]?.push(page);
            }
          } else {
            newStepPages[name] = newStepPages[name]?.filter(
              (p: any) => p !== page
            );
          }
        });
      }
    }
  );

  return { newSteps, newStepPages };
};

const reorderSteps = (newSteps: string[], newStepPages: any) => {
  const originalStepOrder = [
    "Getting Started",
    "Basics",
    "Household Details",
    "Dependents",
    "Business Details",
    "Credits and Deductions",
    "Documents",
    "Refund Instructions",
    "Additional Questions",
    "Review",
    "Disclosure Agreement",
  ];

  return originalStepOrder.reduce((acc: any, step) => {
    if (newSteps.includes(step)) {
      acc[step] = newStepPages[step] || [];
    }
    return acc;
  }, {});
};

const updateDocsToUpload = (state: any, newResponses: any) => {
  const updatedResponses = { ...state.responses, ...newResponses };
  let newDocsToUpload: Record<string, number> = { ...state.docsToUpload };

  const notJointlyMarried = [
    "single",
    "head of household",
    "married filing separately",
  ];

  const isNotJointlyMarried = !notJointlyMarried.includes(
    updatedResponses.personalInfo?.[0]?.filingStatus?.toLowerCase()
  );

  const stepConditions = [
    {
      name: "1099-G",
      condition: () =>
        updatedResponses.personalInfo?.[0]?.incomeType?.some((item: string) =>
          item.includes("unemployment")
        ),
      doc: "1099-G",
      docCount: 1,
    },
    {
      name: "1099-G",
      condition: () =>
        isNotJointlyMarried &&
        updatedResponses.personalInfo?.[0]?.spouseIncomeType?.some(
          (item: string) => item.includes("unemployment")
        ),
      doc: "spouse-1099-G",
      docCount: 1,
    },
    {
      name: "1099-R",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 0
        ),
      doc: "1099-R",
      docCount: 1,
    },
    {
      name: "1099-B",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 1
        ),
      doc: "1099-B",
      docCount: 1,
    },
    {
      name: "1099-INT",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 2
        ),
      doc: "1099-INT",
      docCount: 1,
    },
    {
      name: "1098-T",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 3
        ),
      doc: "1098-T",
      docCount: 1,
    },
    {
      name: "1098-E",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 4
        ),
      doc: "1098-E",
      docCount: 1,
    },
    {
      name: "1095-A",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 5
        ),
      doc: "1095-A",
      docCount: 1,
    },
    {
      name: "1098",
      condition: () =>
        updatedResponses.docInfo?.[0]?.tiles?.some(
          (item: number) => item === 6
        ),
      doc: "1098",
      docCount: 1,
    },
    {
      name: "W-2",
      condition: () =>
        updatedResponses.personalInfo?.[0]?.incomeType?.some((item: string) =>
          item.includes("employee")
        ),
      doc: "W-2",
      docCount: updatedResponses.personalInfo?.[0]?.numJobs || 1,
    },
    {
      name: "W-2",
      condition: () =>
        isNotJointlyMarried &&
        updatedResponses.personalInfo?.[0]?.spouseIncomeType?.some(
          (item: string) => item.includes("employee")
        ),
      doc: "spouse-W-2",
      docCount: updatedResponses.personalInfo?.[0]?.spouseNumJobs || 1,
    },
    {
      name: "1099-NEC",
      condition: () =>
        updatedResponses.personalInfo?.[0]?.incomeType?.some(
          (item: string) => item.includes("freelance") || item.includes("gig")
        ),
      doc: "1099-NEC",
      docCount: updatedResponses.personalInfo?.[0]?.numContractingJobs || 1,
    },
    {
      name: "spouse-1099-NEC",
      condition: () =>
        isNotJointlyMarried &&
        updatedResponses.personalInfo?.[0]?.spouseIncomeType?.some(
          (item: string) => item.includes("freelance") || item.includes("gig")
        ),
      doc: "spouse-1099-NEC",
      docCount:
        updatedResponses.personalInfo?.[0]?.spouseNumContractingJobs || 1,
    },
    {
      name: "id",
      condition: () =>
        updatedResponses.personalInfo?.[0]?.filingStatus
          ?.toLowerCase()
          .includes("married") ||
        updatedResponses.personalInfo?.[0]?.filingStatus
          ?.toLowerCase()
          .includes("spouse"),
      doc: "spouse-id",
      docCount: 1,
    },
  ];

  stepConditions.forEach((condition) => {
    if (condition.condition()) {
      newDocsToUpload[condition.doc] = condition.docCount;
    } else {
      delete newDocsToUpload[condition.doc];
    }
  });

  if (
    updatedResponses.docInfo?.[0]?.tiles?.some((item: number) => item === 10)
  ) {
    const docsToRemove = [
      "1099-R",
      "1099-B",
      "1099-INT",
      "1098-T",
      "1098-E",
      "1095-A",
      "1098",
    ];
    docsToRemove.forEach((doc) => delete newDocsToUpload[doc]);
  }

  return newDocsToUpload;
};

const getDefaultState = () => ({
  taxYear: undefined as number | undefined,
  currentStep: "Getting Started",
  currentPage: "welcome",
  steps: {
    "Getting Started": ["welcome", "personalInfo", "docInfo"],
    Basics: ["identityVerification"],
    Documents: ["uploadedDocs", "uploadDocsOther"],
    "Refund Instructions": ["refundMethod"],
    Review: [OrganizerPages.REVIEW, "comments"],
    "Disclosure Agreement": ["agreement", "confirm"],
  },
  responses: {},
  docsToUpload: { id: 1 },
  status: OrganizerStatus.NOT_STARTED,
  validationErrors: {},
  additionalQuestionsResponses: {},
  activeTab: "0",
  editingReviewPage: false,
  isDetailedForm: true,
  additionalQuestionsConfig: {},
  isDocumentProcessing: false,
  // Async operation states
  isLoadingOrganizer: false,
  isSavingProgress: false,
  organizerError: null as Error | null,
  saveError: null as Error | null,
  // Data from backend
  forms: {},
  allTaxYears: [] as string[],
  organizerCompleteDate: null as string | null,
  // Metadata
  lastFetchedAt: null as number | null,
  userId: null as string | null,
});

export const useTaxOrganizerStore = create<TaxOrganizerData>()((set, get) => ({
  ...getDefaultState(),
  setTaxYear: (year: number) => {
    const currentYear = get().taxYear;
    const currentResponses = get().responses;
    const hasResponses =
      currentResponses && Object.keys(currentResponses).length > 0;

    console.log("Store - setTaxYear called:", {
      currentYear,
      newYear: year,
      hasResponses,
      responsesCount: hasResponses ? Object.keys(currentResponses).length : 0,
    });

    // Reset store if:
    // 1. Current year exists and is different from new year, OR
    // 2. Current year is undefined but we have responses (stale data from different year)
    const shouldReset =
      (currentYear !== undefined && currentYear !== year) ||
      (currentYear === undefined && hasResponses);

    if (shouldReset) {
      console.log("Store - Resetting store for new year");
      const newState = {
        ...getDefaultState(),
        taxYear: year,
      };
      console.log("Store - New state after reset:", newState);
      set(newState);
    } else {
      console.log("Store - Just updating taxYear without reset");
      set({ taxYear: year });
    }
  },
  setAdditionalQuestionsConfig: (questions) =>
    set({ additionalQuestionsConfig: questions }),
  uploadUIState: {},
  setIsDetailedForm: (isDetailed) => set({ isDetailedForm: isDetailed }),
  setIsDocumentProcessing: (isDocumentProcessing: boolean) =>
    set({ isDocumentProcessing }),
  setEditingReviewPage: (editing: boolean) =>
    set(() => ({
      editingReviewPage: editing,
    })),
  goBackToReviewPage: () =>
    set(() => ({
      currentStep: "Review",
      currentPage: OrganizerPages.REVIEW,
      editingReviewPage: false,
    })),
  setValidationErrors: (errors) =>
    set((state) => ({
      validationErrors:
        typeof errors === "function" ? errors(state.validationErrors) : errors,
    })),
  updateAdditionalQuestionsResponses: (docs) =>
    set((state) => ({
      additionalQuestionsResponses: {
        ...state.additionalQuestionsResponses,
        ...docs,
      },
    })),
  setActiveTab: (tab: string) => set({ activeTab: tab }),
  setCurrentStep: (step) =>
    set((state) => {
      const firstPageOfStep = state.steps[step]?.[0] || state.currentPage;
      return {
        currentStep: step,
        currentPage: firstPageOfStep,
      };
    }),
  setCurrentPage: (page) => set({ currentPage: page }),
  updateResponses: (newResponses: Partial<any>) =>
    set((state) => {
      const updatedResponses = {
        ...state.responses,
        ...newResponses,
      };

      state.updateSteps(updatedResponses);

      return {
        responses: updatedResponses,
      };
    }),
  updateDocsToUpload: (newResponses) =>
    set((state) => ({
      docsToUpload: updateDocsToUpload(state, newResponses),
    })),
  setStatus: (status: OrganizerStatus) => set({ status }),
  setInitialData: (data) => set(data),
  updateSteps: (newResponses, isDetailedForm?: boolean) =>
    set((state) => {
      const updatedResponses = {
        ...state?.responses,
        ...newResponses,
      };

      const { newSteps, newStepPages } = processStepConditions(
        updatedResponses,
        state.steps,
        isDetailedForm ?? state.isDetailedForm,
        state.additionalQuestionsConfig
      );

      const updatedSteps = reorderSteps(newSteps, newStepPages);
      const validPages = Object.values(updatedSteps).flat();

      // Properties that should always be preserved regardless of step visibility
      // These are data containers, not pages, or conditional pages that should persist
      const alwaysPreserveProperties = [
        "identityVerification", // Personal identification data
        "refundMethod", // Banking/refund information
        "spouseInfo", // Spouse data
        "dependentsInfo", // Dependents array
        "daycareInfo", // Daycare information (conditional page)
        "businessInfo", // Business details (conditional page)
        "formerAddress", // Former address for people who moved (conditional page)
        "credits", // Charitable giving deductions (conditional page)
        "medicalExpenses", // Medical expense deductions (conditional page)
        "uploadedDocs", // Document metadata
        "comments", // User comments
      ];

      const cleanedResponses = Object.fromEntries(
        Object.entries(state.responses).filter(
          ([page]) =>
            validPages.includes(page) || alwaysPreserveProperties.includes(page)
        )
      );

      const pageToUploadMap: Record<string, string[]> = {
        businessInfo: ["business"],
        credits: ["credits"],
        medicalExpenses: ["medical"],
        spouseInfo: [
          "spouse-id",
          "spouse-1099-G",
          "spouse-1099-NEC",
          "spouse-W-2",
        ],
        // Add other page-to-upload mappings as needed
      };

      const uploadedDocs = cleanedResponses.uploadedDocs || {};
      const cleanedUploadedDocs: Record<string, any> = { ...uploadedDocs };

      Object.entries(pageToUploadMap).forEach(([page, docKeys]) => {
        if (!validPages.includes(page)) {
          docKeys.forEach((key) => delete cleanedUploadedDocs[key]);
        }
      });

      if (!validPages.includes("formerAddress") && cleanedResponses.comments) {
        const { formerAddress, ...otherComments } =
          cleanedResponses.comments as Record<string, any>;
        cleanedResponses.comments = otherComments;
      }

      return {
        steps: updatedSteps,
        responses: {
          ...cleanedResponses,
          uploadedDocs: cleanedUploadedDocs,
        },
      };
    }),
  goToNextPage: () =>
    set((state) => {
      const currentStepPages = state.steps[state.currentStep];
      const currentPageIndex = currentStepPages.indexOf(state.currentPage);
      const nextPageIndex = currentPageIndex + 1;

      if (nextPageIndex < currentStepPages.length) {
        return {
          currentPage: currentStepPages[nextPageIndex],
        };
      } else {
        const stepNames = Object.keys(state.steps);
        const currentStepIndex = stepNames.indexOf(state.currentStep);
        const nextStepIndex = currentStepIndex + 1;

        if (nextStepIndex < stepNames.length) {
          const nextStep = stepNames[nextStepIndex];
          return {
            currentStep: nextStep,
            currentPage: state.steps[nextStep][0],
          };
        } else {
          return { status: OrganizerStatus.COMPLETE };
        }
      }
    }),
  getValidationParams: () => {
    const state = get();
    return {
      currentPage: state.currentPage,
      responses: state.responses,
      currentStep: state.currentStep,
      docsToUpload: state.docsToUpload,
      additionalQuestionsResponses: state.additionalQuestionsResponses,
      additionalQuestionsConfig: state.additionalQuestionsConfig,
      validationErrors: state.validationErrors,
      setValidationErrors: state.setValidationErrors,
      setActiveTab: state.setActiveTab,
    };
  },
  getProgressParams: () => {
    // Using the get function passed to the store creator
    const state = get();
    return {
      steps: state.steps,
      responses: state.responses,
      status: state.status,
      docsToUpload: state.docsToUpload,
      additionalQuestionsResponses: state.additionalQuestionsResponses,
      additionalQuestionsConfig: state.additionalQuestionsConfig,
    };
  },
  resetStore: () => {
    console.log("Store - Resetting store to default state");
    set(getDefaultState());
  },

  /**
   * Fetch organizer data from backend and merge with sensitive data
   */
  fetchOrganizerData: async (userId: string, taxYear: number, client?: any) => {
    // Validation
    if (!userId || !taxYear) {
      throw new Error("userId and taxYear are required");
    }

    const state = get();

    // Prevent duplicate fetches
    if (state.isLoadingOrganizer) {
      console.log("Already loading organizer data, skipping duplicate fetch");
      return;
    }

    console.log("fetchOrganizerData - Starting fetch:", { userId, taxYear });

    // Set loading state
    set({
      isLoadingOrganizer: true,
      organizerError: null,
      userId,
      taxYear,
    });

    try {
      // Parallel API calls
      const [
        organizerResponse,
        sensitiveDataResponse,
        additionalQuestionsResponse,
      ] = await Promise.all([
        fetchOrganizerDataFromAPI(userId, taxYear),
        fetchSensitiveInformation(userId, taxYear),
        axios
          .get(`${backend_url}${Endpoints.additionalQuestions}`, AXIOS_CONFIG)
          .catch((error: any) => {
            console.warn("Failed to fetch additional questions config:", error);
            return { data: { content: {} } }; // Return empty config on error
          }),
      ]);

      // Get user context
      const { personalInfo, businessInfo, role } = getUserInfo();
      const forms = organizerResponse?.data?.content?.forms ?? {};
      let allTaxYears = organizerResponse?.data?.content?.allTaxYears ?? [];

      // Remove years below 2023
      const filteredTaxYears = allTaxYears.filter(
        (year: string) => year > "2022"
      );

      const organizerData = organizerResponse?.data?.content?.organizer ?? {};
      const sensitiveData = sensitiveDataResponse?.content;
      const detailedFlow = businessInfo?.detailedFlow ?? true;

      // Get name and phone
      const { firstName, lastName, phoneNumber } = getNameAndPhone(
        role || null,
        personalInfo,
        client
      );

      // Merge sensitive data if responses exist
      if (organizerData?.responses) {
        updateIdentityVerification(
          organizerData,
          sensitiveData,
          firstName,
          lastName,
          phoneNumber
        );
        updateSensitiveData(organizerData, sensitiveData);
      }

      // Ensure taxYear is set in organizer data
      const dataWithYear = { ...organizerData, taxYear };

      // Update store with all data
      set({
        responses: dataWithYear.responses || {},
        forms,
        allTaxYears: filteredTaxYears,
        isDetailedForm: detailedFlow,
        isLoadingOrganizer: false,
        lastFetchedAt: Date.now(),
        status: dataWithYear.status || OrganizerStatus.NOT_STARTED,
        currentStep: dataWithYear.currentStep || state.currentStep,
        currentPage: dataWithYear.currentPage || state.currentPage,
        docsToUpload: dataWithYear.docsToUpload || state.docsToUpload,
        additionalQuestionsResponses:
          dataWithYear.additionalQuestionsResponses ||
          state.additionalQuestionsResponses,
        additionalQuestionsConfig:
          additionalQuestionsResponse?.data?.content || {},
        organizerCompleteDate:
          dataWithYear.organizerCompleteDate || state.organizerCompleteDate,
      });

      // Update derived state
      get().updateSteps(dataWithYear.responses || {}, detailedFlow);
      get().updateDocsToUpload(dataWithYear.responses || {});

      console.log("fetchOrganizerData - Success");

      // Check engagement letter status and show modal if needed
      const organizerStatus =
        dataWithYear.status || OrganizerStatus.NOT_STARTED;
      await useEngagementLetterStore
        .getState()
        .checkAndShowModal(taxYear, organizerStatus);
    } catch (error) {
      console.error("fetchOrganizerData - Error:", error);
      set({
        organizerError: error as Error,
        isLoadingOrganizer: false,
        responses: {},
      });
      throw error;
    }
  },

  /**
   * Save progress to backend
   */
  saveProgress: async (
    newResponses: Partial<any>,
    newStatus?: Status,
    newStep?: string,
    newPage?: string
  ) => {
    const state = get();

    // Validation
    if (!state.taxYear || !state.userId) {
      throw new Error(
        "Cannot save: taxYear or userId not set. Call fetchOrganizerData first."
      );
    }

    // Prevent concurrent saves
    if (state.isSavingProgress) {
      console.log("Already saving progress, skipping duplicate save");
      return;
    }

    console.log("[OrganizerSave] Starting save");

    // Set saving state
    set({
      isSavingProgress: true,
      saveError: null,
    });

    // Store previous state for rollback
    const previousState = {
      responses: state.responses,
      status: state.status,
      currentStep: state.currentStep,
      currentPage: state.currentPage,
    };

    try {
      // Merge new data
      const updatedResponses = {
        ...state.responses,
        ...newResponses,
      };

      // Optimistic update - update local store immediately
      set({
        responses: updatedResponses,
        status: newStatus ?? state.status,
        currentStep: newStep ?? state.currentStep,
        currentPage: newPage ?? state.currentPage,
      });

      // Update derived state
      get().updateSteps(updatedResponses);
      get().updateDocsToUpload(updatedResponses);

      // Prepare data for backend
      const dataToSave = {
        responses: updatedResponses,
        status: newStatus ?? state.status,
        currentStep: newStep ?? state.currentStep,
        currentPage: newPage ?? state.currentPage,
        docsToUpload: get().docsToUpload,
        additionalQuestionsResponses: state.additionalQuestionsResponses,
        steps: get().steps,
        taxYear: state.taxYear,
      };

      // Persist to backend
      await saveTaxOrganizerDataToAPI(dataToSave, state.taxYear);

      set({ isSavingProgress: false });

      console.log("saveProgress - Success");
    } catch (error) {
      console.error("saveProgress - Error:", error);

      // Rollback optimistic update
      set({
        responses: previousState.responses,
        status: previousState.status,
        currentStep: previousState.currentStep,
        currentPage: previousState.currentPage,
        saveError: error as Error,
        isSavingProgress: false,
      });

      // Re-calculate derived state with previous data
      get().updateSteps(previousState.responses);
      get().updateDocsToUpload(previousState.responses);

      throw error;
    }
  },

  /**
   * Refetch organizer data using stored userId and taxYear
   */
  refetchOrganizer: async () => {
    const { userId, taxYear } = get();
    if (!userId || !taxYear) {
      throw new Error("Cannot refetch: userId or taxYear not set");
    }
    await get().fetchOrganizerData(userId, taxYear);
  },

  /**
   * Clear all organizer data and reset to defaults
   */
  clearOrganizerData: () => {
    console.log("Clearing organizer data");
    set({
      ...getDefaultState(),
    });
  },
}));
